import React from "react";
import { useForm, FormContext } from "react-hook-form";
import { TextField, AbstractControlledField } from "_c/form/Field";
import PT from "prop-types";
import { Button } from "_c/button";
import CameraIcon from "_c/icon/Camera";
import { generateUrl } from "@/utils/cloudinary";
import Select from "_c/Select";
import { useCloudinaryWidget } from "@/hooks/useCloudinaryWidget";
import { createPortfolioSchema } from "@/validation/portfolio";
import Modal, { Footer, Content, Header } from "_c/Modals";
import { analyticsAction } from "@/utils/analytics";
import isempty from "lodash/isEmpty";
import { useMut } from "@/hooks/useMut";

const UploadField = ({ onChange, value, t, ...props }) => {
  const { uploadPhoto } = useCloudinaryWidget();
  const onClick = async () => {
    if (props.type === "saloon") {
      analyticsAction({
        category: "salon_profile",
        action: "photo_add",
      });
    }
    const photos = await uploadPhoto({ multiple: true });
    if (!isempty(photos)) {
      onChange([...(value || []), ...photos]);
    }
  };
  return (
    <>
      <Button
        aria-label={t("common:add_photo")}
        onClick={onClick}
        withIcon
        medium
        {...props}
        type="button"
      >
        <CameraIcon color="white" />
        <span>{t("common:add_photo")}</span>
      </Button>
      {!isempty(value) && (
        <ul
          css={`
            margin-top: 10px;
            display: flex;
            flex-wrap: wrap;
            align-items: flex-start;
          `}
        >
          {value.map((photo) => (
            <li
              css={`
                position: relative;
                width: calc(33.333% - 2px);
                padding-bottom: calc(33.333% - 2px);
                margin: 1px;
              `}
              key={photo}
            >
              <img
                key={photo}
                css={`
                  position: absolute;
                  left: 0;
                  top: 0;
                  width: 100%;
                  height: 100%;
                  object-fit: cover;
                  border-radius: var(--root-br);
                `}
                src={generateUrl(photo, {
                  width: 300,
                  height: 300,
                  crop: "fill",
                })}
              />
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

const ns = "common:portfolio.upload_form";
const PortfolioUploadForm = ({ t, ...props }) => {
  const methods = useForm({
    validationSchema: createPortfolioSchema,
  });
  const { handleSubmit, formState } = methods;
  const { dirty } = formState;
  const submitMut = useMut(
    async (form) => {
      for (const photo of form.photo) {
        const norm = {
          desc: form.desc,
          service: form.service?.value,
          photo,
        };
        await props.onSubmit(norm);
      }
    },
    {
      t,
      onSuccess: props.onClose,
      notifyOnError: true,
    }
  );
  const onSubmit = (form) => submitMut.mutate(form);

  return (
    <>
      <Header onClick={props.onClose}>{t("common:close")}</Header>
      <Content>
        <FormContext {...methods}>
          <form id="upload-portfolio-form" onSubmit={handleSubmit(onSubmit)}>
            <AbstractControlledField
              t={t}
              label={t("common:photo")}
              name="photo"
              id="portfolio-upload__photo"
              Component={UploadField}
              type={props.type}
            />
            <TextField
              maxLength="512"
              t={t}
              placeholder={t(`${ns}.desc_placeholder`)}
              name="desc"
              label={t("common:desc")}
              as="textarea"
              rows="4"
              id="portfolio-upload__desc"
            />
            {props.showServiceSelect && (
              <div
                css={`
                  padding-bottom: 200px;
                `}
              >
                <AbstractControlledField
                  t={t}
                  name="service"
                  label={t("common:service")}
                  id="portfolio-upload__service"
                  placeholder={t(`${ns}.service_placeholder`)}
                  Component={Select}
                  isClearable
                  options={props.services}
                />
              </div>
            )}
          </form>
        </FormContext>
      </Content>
      <Footer
        form="upload-portfolio-form"
        type="submit"
        disabled={submitMut.isLoading || !dirty}
      >
        {t("common:add")}
      </Footer>
    </>
  );
};
PortfolioUploadForm.propTypes = {
  showServiceSelect: PT.bool,
  isOpen: PT.bool,
  type: PT.oneOf(["client", "master", "saloon"]),

  t: PT.func.isRequired,
  onSubmit: PT.func.isRequired,
  onClose: PT.func.isRequired,
};

const UploadModal = (props) => {
  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose}>
      <PortfolioUploadForm {...props} />
    </Modal>
  );
};
export default UploadModal;
